/**
 * ============================================================================
 * VIEWSTATE STORAGE ABSTRACTION
 * ============================================================================
 *
 * Flexible storage interface for ViewState that allows easy migration
 * from sessionStorage to IndexedDB (or any other storage backend).
 *
 * Current Implementation: SessionStorage
 * Future: Can be swapped to IndexedDB by changing the adapter
 */

import {
  compressViewState,
  decompressViewState,
  deserializeViewState,
  type SerializedViewState,
} from './storage-helpers';

import { ViewState } from '@controlrooms/models';

/**
 * ============================================================================
 * STORAGE ADAPTER INTERFACE
 * ============================================================================
 *
 * All ViewState storage implementations must implement this interface.
 * This allows easy swapping between storage backends.
 */
export interface ViewStateStorageAdapter {
  /**
   * Store ViewState
   * @param key - Storage key (viewId for saved views, tabId for unsaved)
   * @param viewState - ViewState to store
   */
  store(key: string, viewState: ViewState): Promise<void>;

  /**
   * Retrieve ViewState
   * @param key - Storage key to retrieve
   * @returns ViewState or null if not found
   */
  retrieve(key: string): Promise<ViewState | null>;

  /**
   * Remove ViewState
   * @param key - Storage key to remove
   */
  remove(key: string): Promise<void>;

  /**
   * Check if ViewState exists
   * @param key - Storage key to check
   */
  exists(key: string): Promise<boolean>;

  /**
   * Clear all ViewStates for a tenant (optional)
   * @param tenantId - Tenant ID to clear
   */
  clearTenant?(tenantId: number): Promise<void>;

  /**
   * Remove all ViewState entries except those with id in keepIds (optional)
   */
  removeAllExcept?(keepIds: Set<string>): Promise<void>;
}

/**
 * ============================================================================
 * SESSIONSTORAGE ADAPTER (Current Implementation)
 * ============================================================================
 */
class SessionStorageViewStateAdapter implements ViewStateStorageAdapter {
  private readonly prefix: string;
  private readonly compressedFlag: string;

  constructor(prefix = 'tab-', compressedFlag = '-compressed') {
    this.prefix = prefix;
    this.compressedFlag = compressedFlag;
  }

  async store(key: string, viewState: ViewState): Promise<void> {
    const storageKey = `${this.prefix}${key}`;
    const compressedFlagKey = `${storageKey}${this.compressedFlag}`;

    try {
      const compressed = compressViewState(viewState);
      const isCompressed = compressed.startsWith('compressed:');

      sessionStorage.setItem(storageKey, compressed);
      if (isCompressed) {
        sessionStorage.setItem(compressedFlagKey, 'true');
      } else {
        sessionStorage.removeItem(compressedFlagKey);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.error('SessionStorage quota exceeded for ViewState:', key);
        throw new Error(`Storage quota exceeded for key: ${key}`);
      }
      throw error;
    }
  }

  async retrieve(key: string): Promise<ViewState | null> {
    const storageKey = `${this.prefix}${key}`;
    const compressedFlagKey = `${storageKey}${this.compressedFlag}`;

    const stored = sessionStorage.getItem(storageKey);
    if (!stored) {
      return null;
    }

    try {
      const isCompressed = sessionStorage.getItem(compressedFlagKey) === 'true';
      const decompressed = isCompressed ? decompressViewState(stored) : stored;
      const parsed = JSON.parse(decompressed) as unknown;

      // Validate basic structure
      if (!parsed || typeof parsed !== 'object') {
        console.error('Invalid ViewState structure for key:', key);
        return null;
      }

      // Type guard for SerializedViewState
      const serialized = parsed as SerializedViewState;
      if (
        typeof serialized.viewId !== 'string' ||
        typeof serialized.selectedMode !== 'string' ||
        typeof serialized.name !== 'string' ||
        typeof serialized.tenant_id !== 'number' ||
        !serialized.view ||
        !serialized.timeSelection ||
        !serialized.config
      ) {
        console.error('Invalid ViewState structure for key:', key);
        return null;
      }

      return deserializeViewState(serialized);
    } catch (error) {
      console.error('Failed to restore ViewState for key:', key, error);
      return null;
    }
  }

  async remove(key: string): Promise<void> {
    const storageKey = `${this.prefix}${key}`;
    const compressedFlagKey = `${storageKey}${this.compressedFlag}`;

    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(compressedFlagKey);
  }

  async exists(key: string): Promise<boolean> {
    const storageKey = `${this.prefix}${key}`;
    return sessionStorage.getItem(storageKey) !== null;
  }

  async clearTenant(tenantId: number): Promise<void> {
    // Clear all ViewStates for this tenant
    // This is a simple implementation - can be optimized
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        // Try to retrieve and check tenant_id
        try {
          const viewState = await this.retrieve(key.replace(this.prefix, ''));
          if (viewState && viewState.tenant_id === tenantId) {
            keysToRemove.push(key);
          }
        } catch {
          // Skip invalid entries
        }
      }
    }

    for (const key of keysToRemove) {
      sessionStorage.removeItem(key);
      sessionStorage.removeItem(`${key}${this.compressedFlag}`);
    }
  }

  /**
   * Remove all ViewState entries except those with id in keepIds.
   * Used by "close other tabs" to clear tab-<uuid> storage except current tab.
   */
  async removeAllExcept(keepIds: Set<string>): Promise<void> {
    const idsToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.prefix) && !key.endsWith(this.compressedFlag)) {
        const id = key.slice(this.prefix.length);
        if (!keepIds.has(id)) {
          idsToRemove.push(id);
        }
      }
    }
    await Promise.all(idsToRemove.map((id) => this.remove(id)));
  }
}

/**
 * ============================================================================
 * VIEWSTATE STORAGE MANAGER
 * ============================================================================
 *
 * Main interface for ViewState storage operations.
 * Uses the configured adapter (currently SessionStorage).
 */
class ViewStateStorageManager {
  private adapter: ViewStateStorageAdapter;

  constructor(adapter?: ViewStateStorageAdapter) {
    // Default to SessionStorage adapter
    this.adapter = adapter || new SessionStorageViewStateAdapter();
  }

  /**
   * Set a custom storage adapter (for future IndexedDB migration)
   */
  setAdapter(adapter: ViewStateStorageAdapter): void {
    this.adapter = adapter;
  }

  /**
   * Get the current adapter (for testing/debugging)
   */
  getAdapter(): ViewStateStorageAdapter {
    return this.adapter;
  }

  /**
   * Store ViewState
   * Uses viewId for saved views, tabId for unsaved tabs
   */
  async storeViewState(tabId: string, viewState: ViewState): Promise<void> {
    // Determine storage key: use viewId for saved views, tabId for unsaved
    const storageKey = viewState.viewId && viewState.viewId !== tabId ? viewState.viewId : tabId;

    await this.adapter.store(storageKey, viewState);
  }

  /**
   * Restore ViewState
   * Tries viewId first (for saved views), then tabId (for unsaved tabs)
   */
  async restoreViewState(tabId: string, viewId?: string): Promise<ViewState | null> {
    // Try viewId first (saved views), then tabId (unsaved tabs)
    const identifiers = viewId && viewId !== tabId ? [viewId, tabId] : [tabId];

    for (const identifier of identifiers) {
      const viewState = await this.adapter.retrieve(identifier);
      if (viewState) {
        return viewState;
      }
    }

    return null;
  }

  /**
   * Remove ViewState
   * Removes both by viewId (if provided) and tabId to handle migration
   */
  async removeViewState(tabId: string, viewId?: string): Promise<void> {
    // Remove both viewId and tabId entries
    const identifiers = viewId && viewId !== tabId ? [viewId, tabId] : [tabId];

    await Promise.all(identifiers.map((id) => this.adapter.remove(id)));
  }

  /**
   * Check if ViewState exists
   */
  async viewStateExists(tabId: string, viewId?: string): Promise<boolean> {
    const identifiers = viewId && viewId !== tabId ? [viewId, tabId] : [tabId];

    for (const identifier of identifiers) {
      if (await this.adapter.exists(identifier)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Clear all ViewStates for a tenant
   */
  async clearTenantViewStates(tenantId: number): Promise<void> {
    if (this.adapter.clearTenant) {
      await this.adapter.clearTenant(tenantId);
    }
  }

  /**
   * Remove all ViewState entries except for the given tab (and its viewId if saved).
   * Used by "close other tabs" so we don't loop closeTab (which navigates and loses context).
   */
  async removeAllViewStatesExcept(keepTabId: string, keepViewId?: string): Promise<void> {
    const keepIds = new Set<string>([keepTabId]);
    if (keepViewId && keepViewId !== keepTabId) {
      keepIds.add(keepViewId);
    }
    if (this.adapter.removeAllExcept) {
      await this.adapter.removeAllExcept(keepIds);
    }
  }
}

/**
 * ============================================================================
 * SINGLETON INSTANCE
 * ============================================================================
 *
 * Export a singleton instance that can be used throughout the app.
 * The adapter can be swapped at runtime if needed.
 */
export const viewStateStorage = new ViewStateStorageManager();

/**
 * ============================================================================
 * CONVENIENCE FUNCTIONS (Backward Compatibility)
 * ============================================================================
 *
 * These functions maintain backward compatibility with existing code
 * while using the new storage abstraction.
 */
export async function storeViewState(tabId: string, viewState: ViewState): Promise<void> {
  return viewStateStorage.storeViewState(tabId, viewState);
}

export async function restoreViewState(tabId: string, viewId?: string): Promise<ViewState | null> {
  return viewStateStorage.restoreViewState(tabId, viewId);
}

export async function removeViewState(tabId: string, viewId?: string): Promise<void> {
  return viewStateStorage.removeViewState(tabId, viewId);
}

/**
 * Clear all ViewStates for a tenant (e.g. on tenant switch).
 */
export async function clearTenantViewStates(tenantId: number): Promise<void> {
  return viewStateStorage.clearTenantViewStates(tenantId);
}

/**
 * Remove all ViewState entries (tab-<id>) except for the given tab.
 * Used by close-other-tabs so persistence and local state stay in sync without navigating.
 */
export async function removeAllViewStatesExcept(
  keepTabId: string,
  keepViewId?: string,
): Promise<void> {
  return viewStateStorage.removeAllViewStatesExcept(keepTabId, keepViewId);
}
